// React i hooks
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Navigacija
import { Routes } from "../../navigation";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// API
import { getListingVehiclesAPI } from "services/api/sales/listings/api";
import {
  FleetVehicleRow,
  FleetVehicleSearchFormValues,
} from "services/api/fleet/vehicles/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Komponente
import { MarketplaceSearchPageCmp } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { NoResults } from "ui/components/NoResults";
import useQueryParam from "services/hooks/useQueryParam";

// Stranice / Komponente pretrage
import { MarketplaceSearchResults } from "./components/MarketplaceSearchResults";
import { MarketplaceSearchFilters } from "./components/MarketplaceSearchFilters";

// Konstante
import { MARKETPLACE_SEARCH_PAGE_TITLE, MARKETPLACE_SEARCH_TITLE } from "ui/components/PageTitle/consts";

// Hooks
import { useSelectedStringItemsChanges } from "./hooks";

const MarketplaceSearchPage: MarketplaceSearchPageCmp = () => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Grid je default view za marketplace
  const [viewType, setViewType] = useQueryParam("view", "grid", ["table", "grid"] as const);
  const searchRequestIdRef = useRef(0);
  const navigate = useNavigate();

  const handleViewTypeChange = useCallback((value: string) => {
    setViewType(value as "table" | "grid");
  }, [setViewType]);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    filters?: FleetVehicleSearchFormValues
  ) => {
    const requestId = ++searchRequestIdRef.current;
    setIsLoading(true);
    try {
      // Marketplace prikazuje samo vozila s oglasom AKTIVNIM na marketplace kanalu, ne cijelu flotu.
      const result = await getListingVehiclesAPI(
        pagination,
        filters?.search,
        filters?.vehicleManufacturerId,
        filters?.vehicleModelId,
        filters?.vehicleTypeId,
        "marketplace"
      );

      if (requestId !== searchRequestIdRef.current) return;
      setSearchResults(result.vehicles);
    } catch {
      if (requestId !== searchRequestIdRef.current) return;
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    if (requestId !== searchRequestIdRef.current) return;
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.MarketplaceDetailPageRouter, {
        id,
      })
    );
  }, []);

  const handleSearchChange = useCallback(
    (formValues: FleetVehicleSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, formValues);
    },
    []
  );

  const handleSelectedChange = useSelectedStringItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={MARKETPLACE_SEARCH_TITLE}
              icon={MARKETPLACE_SEARCH_PAGE_TITLE.icon}
              iconColor={MARKETPLACE_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <MarketplaceSearchFilters
            onSearchChange={handleSearchChange}
            viewType={viewType}
            onViewTypeChange={handleViewTypeChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.vehicle.search.noresults")} />
          ) : (
            <MarketplaceSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              selectableRows={true}
              viewType={viewType}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
    </>
  );
};

MarketplaceSearchPage.route = Routes.MarketplaceSearchPageRouter;

export default MarketplaceSearchPage;
